import { useTranslation } from 'react-i18next';
import NumberFlow from '@number-flow/react';

import { DEFAULT_CURRENCY, formatCurrency } from 'support/helpers/currency/currency';
import { DocumentType } from 'support/types';

import {
  useCreditNoteSummaryValues,
  useInvoiceSummaryValues,
} from '../DocumentSummary/hooks/useAccountingDocumentSummary';

import { useDocumentCurrency } from '../helpers';
import { SummaryAccordion } from '../SummaryAccordion/SummaryAccordion';

export const CreateInvoiceAndCreditNoteSummary = ({
  documentType,
}: {
  documentType: DocumentType.invoice | DocumentType.creditNote;
}) => {
  const { t } = useTranslation();
  const currency = useDocumentCurrency();
  const invoiceSummaryValues = useInvoiceSummaryValues();
  const creditNoteSummary = useCreditNoteSummaryValues();
  const values = documentType === DocumentType.invoice ? invoiceSummaryValues : creditNoteSummary;

  const isInvoice = documentType === DocumentType.invoice;

  return (
    <SummaryAccordion
      header={{
        label: t('webedi:documentSummary.title', {
          documentType: t(`common:messageTypes.singular.${documentType}`),
        }),
        value: (
          <NumberFlow value={values.due || 0} format={{ style: 'currency', currency: currency || DEFAULT_CURRENCY }} />
        ),
      }}
      bodyItems={[
        {
          label: isInvoice
            ? t('webedi:documentSummary.invoice.grossAmount.label')
            : t('webedi:documentSummary.creditNote.grossAmount.label'),
          value: formatCurrency(values.subtotal, currency),
        },
        {
          label: isInvoice
            ? t('webedi:documentSummary.invoice.allowances.label')
            : t('webedi:documentSummary.creditNote.allowances.label'),
          value: formatCurrency(values.allowances, currency),
        },
        {
          label: isInvoice
            ? t('webedi:documentSummary.invoice.charges.label')
            : t('webedi:documentSummary.creditNote.charges.label'),
          value: formatCurrency(values.charges, currency),
        },
        {
          label: isInvoice
            ? t('webedi:documentSummary.invoice.taxes.label')
            : t('webedi:documentSummary.creditNote.taxes.label'),
          value: formatCurrency(values.taxes, currency),
        },
      ]}
      footer={{
        label: isInvoice
          ? t('webedi:documentSummary.invoice.footer.label')
          : t('webedi:documentSummary.creditNote.footer.label'),
        value: formatCurrency(values.due, currency),
      }}
    />
  );
};
